<template>
    <div id="tags">
        <supsis-table :columns="cols()" :rows="tags.data" :options="tableOptions">
            <template #header>
                <div class="d-flex justify-content-end align-items-center">
                    <supsis-button 
                        v-if="checkPermission(PERMISSION_TYPES['tags.create'].tr.name)"
                        primary 
                        @click="showModalView"
                    >
                        <md-icon style="color: white">add</md-icon>
                        {{ $t("message.addTag") }}
                    </supsis-button>
                </div>
            </template>
        </supsis-table>
        <Modal2
            v-if="isModalShow"
            id="tags-form"
            :title="editMode ? $t('message.updateTag') : $t('message.addTag')"
            :show-footer="false"
            @cancel="hideModal"
            rowKey="_id"
        >
            <a-form class="d-flex flex-column justify-content-center" @submit.prevent="duplicateControl">
                <a-row class="mb-3">
                    <a-col>
                        <a-form-item :label="$t('message.tagName')">
                            <a-input v-model="form.name" :placeholder="$t('message.tagName')" />
                        </a-form-item>
                        <a-form-item :label="$t('message.tagDescription')">
                            <a-textarea v-model="form.description" :placeholder="$t('message.tagDescription')" />
                        </a-form-item>

                        <a-form-item :label="$t('message.tagColor')">
                            <a-input type="color" v-model="form.color" />
                        </a-form-item>
                    </a-col>
                </a-row>
                <a-row>
                    <a-col id="buttons">
                        <a-button @click="hideModal">
                            {{ $t("message.cancel") }}
                        </a-button>
                        <a-button
                            v-if="!editMode ? checkPermission(PERMISSION_TYPES['tags.create'].tr.name) : checkPermission(PERMISSION_TYPES['tags.update'].tr.name)"
                            :disabled="loading"
                            type="submit"
                            variant="primary"
                            @click="duplicateControl"
                            class="btn-primary"
                        >
                            <template v-if="!loading">
                                <span v-if="!editMode">
                                    {{ $t("message.save") }}
                                </span>
                                <span v-else>
                                    {{ $t("message.update") }}
                                </span>
                            </template>
                            <template v-else>
                                <font-awesome-icon icon="spinner" spin />
                            </template>
                        </a-button>
                    </a-col>
                </a-row>
            </a-form>
        </Modal2>
    </div>
</template>


<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import ActionEdit from "../../../../../components/svg/ActionEdit.vue";
import ActionDelete from "../../../../../components/svg/ActionDelete.vue";
import _ from "lodash";
import TagDialog from "../../../../../components/TagDialog.vue";
import { sendSocketMessage } from "../../../../../utils/socketStream";
import Modal2 from "../../../../../components/common/Modal2.vue";
import SupsisTable from "../../../../../components/SupsisTable.vue";
import SupsisButton from "../../components/SupsisButton.vue";
import { PERMISSION_TYPES } from "../../../../../language/permissionTypes";

library.add(faSpinner);
export default {
    name: "Tags",
    components: {
        SupsisButton,
        SupsisTable,
        TagDialog,
        ActionEdit,
        ActionDelete,
        Modal2,
    },
    computed: {
        PERMISSION_TYPES() {
            return PERMISSION_TYPES;
        },
        ...mapState({
            isAdmin: (state) => state.auth.adminUser != null,
            tags: (state) => state.tag.tags,
            site: (state) => state.site.site,
        }),
        ...mapGetters({
            checkPermission: "auth/checkPermission",
        }),
    },
    data() {
        return {
            loading: false,
            editMode: false,
            editId: null,
            tagInitialState: Object.freeze({
                name: "",
                description: "",
                color: "#ffffff",
            }),
            form: {
                name: "",
                description: "",
                color: "#ffffff",
            },
            cols: () => [
                {
                    field: "name",
                    key: "name",
                    title: this.$t("message.tag_name_indicator"),
                },
                {
                    field: "description",
                    key: "description",
                    title: this.$t("message.description"),
                },
                {
                    field: "color",
                    key: "color",
                    title: this.$t("message.color"),
                    copyDisabled: true,
                    renderBodyCell: ({ row }) => {
                        return (
                            <div
                                style={{
                                    backgroundColor: row.color || "#fff",
                                    width: "24px",
                                    height: "24px",
                                    "border-radius": "4px",
                                    "box-shadow": "rgb(97 97 97 / 28%) 0px 0px 8px 3px",
                                }}
                            />
                        );
                    },
                },
                {
                    align: "right",
                    field: "actionsSlot",
                    key: "actionsSlot",
                    title: this.$t("message.transactions"),
                    fixed: "right",
                    copyDisabled: true,
                    renderBodyCell: ({ row }) => {
                        const renderEditButton = () => {
                            return (
                                this.checkPermission(PERMISSION_TYPES["tags.update"].tr.name) && (
                                    <SupsisButton
                                        class="actionButton d-flex align-items-center mr-3"
                                        onClick={() => this.edit(row._id)}
                                        ghost
                                        small
                                    >
                                        <span style="margin-right: 16px; display: block">{this.$t("message.edit")}</span>
                                        <ActionEdit />
                                    </SupsisButton>
                                )
                            );
                        };

                        const renderDeleteButton = () => {
                            return (
                                this.checkPermission(PERMISSION_TYPES["tags.delete"].tr.name) && (
                                    <SupsisButton
                                        class="actionButton d-flex align-items-center"
                                        onClick={() => this.remove(row._id)}
                                        ghost
                                        small
                                    >
                                        <span style="margin-right: 16px; display: block">{this.$t("message.deleteField")}</span>
                                        <ActionDelete />
                                    </SupsisButton>
                                )
                            );
                        };

                        return (
                            <div class="d-flex w-100 justify-content-end">
                                {renderEditButton()}
                                {renderDeleteButton()}
                            </div>
                        );
                    },
                },
            ],
            isModalShow: false,
            queryAction: this.$route.query.action,
            tableOptions: {
                eventCustomOption: {
                    bodyCellEvents: () => {
                        return {
                            dblclick: (event) => {
                                const columnIndex = event.target.cellIndex;
                                const column = this.cols()[columnIndex];
                                if (!column || column.copyDisabled) {
                                    return;
                                }

                                let text = event.toElement.innerText;
                                let textArea = document.createElement("textarea");
                                textArea.value = text;
                                document.body.appendChild(textArea);
                                textArea.select();
                                document.execCommand("copy");
                                document.body.removeChild(textArea);
                                this.$message.success({
                                    content: this.$t("message.copiedClipboard"),
                                    duration: 2,
                                    key: "saved",
                                });
                            },
                        };
                    },
                },
            },
        };
    },
    methods: {
        ...mapActions({
            getTags: "tag/getTags",
            updateTag: "tag/updateTag",
            deleteTag: "tag/deleteTag",
            createTag: "tag/createTag",
        }),
        showModalView() {
            this.editMode = false;
            this.isModalShow = true;
        },
        showModal() {
            this.isModalShow = true;
        },
        hideModal() {
            this.isModalShow = false;
            this.form.name = "";
            this.form.description = "";
            this.form.color = "#ffffff";
        },
        reset() {
            this.hideModal();
            this.form = _.clone(this.tagInitialState);
        },
        duplicateControl() {
            const isThereSameTag = this.tags.data.find(
                (tag) => tag.name.toLowerCase() === this.form.name.toLowerCase()
            );
            const duplicateId = isThereSameTag ? isThereSameTag._id : null;
            const isThisSameTag = duplicateId === this.editId;
            if (!isThisSameTag && duplicateId !== null) {
                this.$toasted.error(this.$t("message.sameTag"), {
                    duration: 3000,
                    position: "bottom-center",
                });
                return;
            } else {
                this.handleSave();
            }
        },
        handleSave() {
            this.loading = true;
            try {
                if (!this.editMode && this.form.name) {
                    const result = this.createTag(this.form);
                    result.then(() => {
                        this.updateNewTags();
                    });
                } else {
                    const result = this.updateTag(this.form);
                    result.then(() => {
                        this.updateNewTags();
                    });
                }
                this.reset();
            } catch (e) {}
            this.loading = false;
        },
        get() {
            try {
                this.getTags().then(() => {
                    if (this.queryAction == "create") this.isModalShow = true;
                });
                log.debug("this.tags.data", this.tags.data);
            } catch (e) {}
        },
        updateNewTags() {
            const socketMessageId = "user tags-updated";
            sendSocketMessage(socketMessageId, {
                siteId: this.site._id,
            });
        },
        remove(id) {
            this.$dialog
                .confirm(this.$t("message.deleteConfirm", { object: this.$t("message.tags") }), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    const result = this.deleteTag(id);
                    result.then(() => {
                        this.updateNewTags();
                    });
                });
        },
        edit(id) {
            const item = this.tags.data.find(tag => tag._id === id);
            if (item) {
                this.form = _.clone(item);
                this.editMode = true;
                this.editId = item._id;
                this.showModal();
            } else {
                console.error("Tag not found with ID:", id);
            }
        },
    },
    mounted() {
        this.get();
    },
};
</script>
<style lang="scss">
#buttons {
    display: flex;
    justify-content: flex-end;
    gap: 7px;
}

#tags-form {
    .ant-form-item label {
        font-weight: 700;
        font-size: 15px;
        color: #9ba2b6;
        line-height: 0px;
    }

    .ant-form legend {
        color: rgba(0, 0, 0, 0.45);
        border-bottom: none;
    }

    .ant-form-item-label {
        line-height: 28px;
    }

    .ant-form-item-label > label::after {
        content: "";
    }

    .ant-radio-wrapper {
        color: #4b5361 !important;
        font-weight: 400 !important;
        height: 14px;
        width: 14px;
        display: inline-flex;
        align-items: center;
        justify-content: space-between;
    }

    .ant-radio-wrapper:last-child {
        margin-left: 50px;
    }

    .ant-radio-wrapper span {
        font-size: 14px;
    }

    .modal-body button {
        width: 160px;
    }

    .btn-primary {
        background-color: #03a9f4;
        border-color: #03a9f4;
        color: #fff;
    }

    .btn-primary:hover {
        background-color: #028fce;
        border-color: #028fce;
        color: #fff;
    }

    .btn-secondary {
        background-color: #6c757d;
        border-color: #6c757d;
        color: #fff;
    }

    .btn-secondary:hover {
        background-color: #596267;
        border-color: #596267;
        color: #fff;
    }
}
</style>
